// src/plugins/sales-forecast/components/ControlPanel.tsx
import React, { useMemo, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../components/ui/card";

import { ResetControl } from "./ResetControl";
import { PredictControl } from "./PredictControl";
import { CloneControl } from "./CloneControl";
import {
  CloneNeighborhoodControl,
  CloneEntry,
} from "./CloneNeighborhoodControl";
import {
  // ConstructionPhaseControl,
  ConstructionPhase,
} from "./ConstructionPhaseControl";
import { centroids, clusterInfo } from "../../../plugins/sales-forecast/clusterConfig";
import { AmenityControl } from "./AmenityControl";
import { PriceRangeControl } from "./PriceRangeControl";
import { InterestRateControl } from "./InterestRateControl";
import { SquareFootRangeControl } from "./SquareFootRangeControl";
import { NeighborhoodCompositionControl } from "./NeighborhoodCompositionControl";
import { PriceChangeControl } from "./PriceChangeControl";
import { BedroomsControl } from "./BedroomsControl";
import { BathsControl } from "./BathsControl";
import { StoriesControl } from "./StoriesControl";
import { GarageTypeControl } from "./GarageTypeControl";
import { GarageSpacesControl } from "./GarageSpacesControl";
import { CityControl, CITY_OPTIONS } from "./CityControl";
import { ActiveCompsControl } from "./ActiveCompsControl";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../styles/theme.css";

export interface Params {
  interestRate: number;
  interestRateThreeMonthChange: number;
  interestRateSixMonthChange: number;
  interestRateNineMonthChange: number;
  interestRateTwelveMonthChange: number;
  interestRateTwoYearChange: number;
  initialSpecs: number;
  specsToStart: number;
  perSpecsSold: number;
  preSolds: number;
  avgPriceMin: number;
  avgPriceMax: number;
  squareFootMin: number;
  squareFootMax: number;
  availableLots: number;
  compositionPct: number;
  amenities: string[];
  threeMonthPriceChange: number;
  sixMonthPriceChange: number;
  nineMonthPriceChange: number;
  twelveMonthPriceChange: number;
  twoYearPriceChange: number;
  bedrooms: number;
  baths: number;
  stories: number;
  yearBuilt: number;
  garageType: string;
  garageSpaces: number;
  city: string;
  lotType: string;
  listingDay: number;
  listingMonth: string;
  neighborhoodID: string;
  neighborhoodTitle: string;
  status: string;
  cloneType: string;
  activeComps: number;
  cloneDate: string;
  constructionPhases: ConstructionPhase[];
  cloneFlag: boolean;
}

export interface ControlPanelProps {
  params: Params;
  setParams: React.Dispatch<React.SetStateAction<Params>>;
  resetDefaults: () => void;
  onForecastClick: () => void;
  onCloneSelected?: (entry: CloneEntry) => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  params,
  setParams,
  resetDefaults,
  onForecastClick,
  onCloneSelected,
}) => {
  const theme = useSystemTheme();

  const handleClone = useCallback(
    (entry: CloneEntry) => {
      const dd = entry.defaults;

      setParams((prev) => ({
        ...prev,
        // preserve current interest rates
        interestRate: prev.interestRate,
        interestRateThreeMonthChange: prev.interestRateThreeMonthChange,
        interestRateSixMonthChange: prev.interestRateSixMonthChange,
        interestRateNineMonthChange: prev.interestRateNineMonthChange,
        interestRateTwelveMonthChange: prev.interestRateTwelveMonthChange,
        interestRateTwoYearChange: prev.interestRateTwoYearChange,

        // neighborhood composition
        initialSpecs: dd.initialSpecs,
        specsToStart: dd.specsToStart,
        perSpecsSold: dd.perSpecsSold,
        preSolds: dd.preSolds,
        availableLots: dd.availableLots,

        // ranges and deltas
        avgPriceMin: dd.avgPriceMin,
        avgPriceMax: dd.avgPriceMax,
        squareFootMin: dd.squareFootMin,
        squareFootMax: dd.squareFootMax,
        compositionPct: dd.composition * 100,
        amenities: [...dd.amenities],
        threeMonthPriceChange: dd.threeMonthPriceChange,
        sixMonthPriceChange: dd.sixMonthPriceChange,
        nineMonthPriceChange: dd.nineMonthPriceChange,
        twelveMonthPriceChange: dd.threeMonthPriceChange,
        twoYearPriceChange: dd.threeMonthPriceChange,
        bedrooms: dd.bedrooms,
        baths: dd.baths,
        stories: dd.stories,
        yearBuilt: dd.yearBuilt,
        garageType: dd.garageType,
        garageSpaces: dd.garageSpaces,
        city: dd.city,
        lotType: dd.lotType,
        listingDay: dd.listingDay,
        listingMonth: dd.listingMonth,
        neighborhoodID: entry.id,
        neighborhoodTitle: entry.title,
        cloneType: entry.cloneType,
        activeComps: (dd as any).activeComps ?? 0,
        cloneDate: dd.cloneDate ?? prev.cloneDate,
      }));

      console.log("[ControlPanel] onClone:", entry.id, entry.title);
      onCloneSelected?.(entry);
    },
    [setParams, onCloneSelected]
  );

  // ✅ Keep this - used by PredictControl
  const handleForecastClick = useCallback(() => {
    onForecastClick();
  }, [onForecastClick]);

  // === Basic parameter handlers ===
  const handleInterestRateChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, interestRate: v })),
    [setParams]
  );
  const handleThreeMonthInterestRateChange = useCallback(
    (v: number) =>
      setParams((p) => ({ ...p, interestRateThreeMonthChange: v })),
    [setParams]
  );
  const handleSixMonthInterestRateChange = useCallback(
    (v: number) =>
      setParams((p) => ({ ...p, interestRateSixMonthChange: v })),
    [setParams]
  );
  const handleNineMonthInterestRateChange = useCallback(
    (v: number) =>
      setParams((p) => ({ ...p, interestRateNineMonthChange: v })),
    [setParams]
  );
  const handleTwelveMonthInterestRateChange = useCallback(
    (v: number) =>
      setParams((p) => ({ ...p, interestRateTwelveMonthChange: v })),
    [setParams]
  );
  const handleTwoYearInterestRateChange = useCallback(
    (v: number) =>
      setParams((p) => ({ ...p, interestRateTwoYearChange: v })),
    [setParams]
  );

  // === Composition handlers ===
  const handleInitialSpecsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, initialSpecs: v })),
    [setParams]
  );
  const handlePreSoldsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, preSolds: v })),
    [setParams]
  );
  const handleAvailableLotsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, availableLots: v })),
    [setParams]
  );

  // Ratio handlers
  const handleSpecsToStartChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, specsToStart: v })),
    [setParams]
  );
  const handlePerSpecsSoldChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, perSpecsSold: v })),
    [setParams]
  );

  // === Price & property handlers ===
  const handleThreeMonthPriceChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, threeMonthPriceChange: v })),
    [setParams]
  );
  const handleSixMonthPriceChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, sixMonthPriceChange: v })),
    [setParams]
  );
  const handleNineMonthPriceChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, nineMonthPriceChange: v })),
    [setParams]
  );
  const handleTwelveMonthPriceChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, twelveMonthPriceChange: v })),
    [setParams]
  );
  const handleTwoYearPriceChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, twoYearPriceChange: v })),
    [setParams]
  );

  const handleAvgPriceRangeChange = useCallback(
    (values: number[]) => {
      const [min = 0, max = 0] = values;
      setParams((p) => ({ ...p, avgPriceMin: min, avgPriceMax: max }));
    },
    [setParams]
  );

  const handleSquareFootRangeChange = useCallback(
    (values: number[]) => {
      const [min = 0, max = 0] = values;
      setParams((p) => ({ ...p, squareFootMin: min, squareFootMax: max }));
    },
    [setParams]
  );

  const handleBedroomsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, bedrooms: v })),
    [setParams]
  );
  const handleBathsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, baths: v })),
    [setParams]
  );
  const handleStoriesChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, stories: v })),
    [setParams]
  );
  const handleGarageTypeChange = useCallback(
    (v: string) => setParams((p) => ({ ...p, garageType: v })),
    [setParams]
  );
  const handleGarageSpacesChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, garageSpaces: v })),
    [setParams]
  );
  const handleCityChange = useCallback(
    (v: string) => setParams((p) => ({ ...p, city: v })),
    [setParams]
  );
  const handleActiveCompsChange = useCallback(
    (v: number) => setParams((p) => ({ ...p, activeComps: v })),
    [setParams]
  );

  const memoizedParams = useMemo(() => ({ ...params }), [params]);

  // === Cluster calc ===
  const featureVector = useMemo<number[]>(() => {
    const pond = memoizedParams.amenities.includes("Pond");
    const largeLots = memoizedParams.amenities.includes("Large Lots");
    const gated = memoizedParams.amenities.includes("Gated");
    const pool = memoizedParams.amenities.includes("Pool");
    const clubhouse = memoizedParams.amenities.includes("Clubhouse");
    const kidsRec =
      memoizedParams.amenities.includes("Playground") ||
      memoizedParams.amenities.includes("Splash Pad");
    const adultsRec = ["Tennis", "Basketball", "Pickleball", "Walking Trails"].some(
      (a) => memoizedParams.amenities.includes(a)
    );

    return [
      pond ? 1 : 0,
      largeLots ? 1 : 0,
      gated ? 1 : 0,
      pool ? 1 : 0,
      clubhouse ? 1 : 0,
      kidsRec ? 1 : 0,
      adultsRec ? 1 : 0,
    ];
  }, [memoizedParams]);

  function squaredDistance(a: number[], b: number[]) {
    return a.reduce((sum, val, i) => sum + Math.pow(val - b[i], 2), 0);
  }

  const cluster = useMemo<number>(() => {
    let best = 0;
    let bestDist = Infinity;
    centroids.forEach((c, i) => {
      const dist = squaredDistance(featureVector, c);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });
    return best;
  }, [featureVector]);

  const clusterName = clusterInfo[cluster].name;
  const clusterDescription = clusterInfo[cluster].description;

  // === Render ===
  return (
    <div className={`control-panel theme-${theme}`}>
      <Card
        className="space-y-4 p-6 shadow-md rounded-xl w-full"
        style={{
          backgroundColor: "var(--card-bg)",
          color: "var(--text-color)",
          border: "1px solid var(--border-color)",
        }}
      >
        <CardHeader>
          <CardTitle
            className="text-2xl font-bold"
            style={{ color: "var(--text-color)" }}
          >
            Simulation Parameters
          </CardTitle>
        </CardHeader>

        <CardContent
          className="space-y-6 shadow-md rounded-xl"
          style={{
            backgroundColor: "var(--card-bg)",
            color: "var(--text-color)",
          }}
        >
          <div className="flex flex-wrap items-center gap-4">
            <CloneNeighborhoodControl onClone={handleClone} />
          </div>

          <div className="grid grid-cols-3 gap-4 w-full">
            <div className="flex justify-start">
              <PredictControl onPredict={handleForecastClick} />
            </div>
            <div className="flex justify-center">
              <CloneControl
                cloneFlag={params.cloneFlag}
                onToggle={() =>
                  setParams((prev) => ({ ...prev, cloneFlag: !prev.cloneFlag }))
                }
              />
            </div>
            <div className="flex justify-end">
              <ResetControl
                onReset={resetDefaults}
                cloneTitle={params.neighborhoodTitle}
              />
            </div>
          </div>

          <hr style={{ borderColor: "var(--border-color)" }} />

          {/* Scrollable section */}
          <div
            className="h-[75vh] space-y-6 w-full overflow-scroll rounded-md p-3"
            style={{
              backgroundColor: "var(--panel-bg)",
              color: "var(--text-color)",
              border: "1px solid var(--border-color)",
            }}
          >
            <InterestRateControl
              initialRate={params.interestRate}
              initialThreeMonthChange={params.interestRate}
              initialSixMonthChange={params.interestRate}
              initialNineMonthChange={params.interestRate}
              initialTwelveMonthChange={params.interestRate}
              initialTwoYearChange={params.interestRate}
              onChange={handleInterestRateChange}
              onChangeThreeMonthChange={handleThreeMonthInterestRateChange}
              onChangeSixMonthChange={handleSixMonthInterestRateChange}
              onChangeNineMonthChange={handleNineMonthInterestRateChange}
              onChangeTwelveMonthChange={handleTwelveMonthInterestRateChange}
              onChangeTwoYearChange={handleTwoYearInterestRateChange}
            />

            <hr style={{ borderColor: "var(--border-color)" }} />

            <NeighborhoodCompositionControl
              initialSpecs={params.initialSpecs}
              preSolds={params.preSolds}
              availableLots={params.availableLots}
              specsToStart={params.specsToStart}
              perSpecsSold={params.perSpecsSold}
              onSpecsChange={handleInitialSpecsChange}
              onPreSoldsChange={handlePreSoldsChange}
              onAvailableLotsChange={handleAvailableLotsChange}
              onSpecsToStartChange={handleSpecsToStartChange}
              onPerSpecsSoldChange={handlePerSpecsSoldChange}
            />

            <hr style={{ borderColor: "var(--border-color)" }} />

            <Card
              className="shadow-md rounded-xl"
              style={{
                backgroundColor: "var(--subcard-bg)",
                color: "var(--text-color)",
                border: "1px solid var(--border-color)",
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: "var(--text-color)" }}>
                  Property Features
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                {/* 🧩 Two-column responsive grid */}
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start"
                  style={{ alignItems: "start" }}
                >
                  {/* Full-width controls */}
                  <div className="sm:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <PriceRangeControl
                      min={0}
                      max={1_000_000}
                      step={1}
                      values={[params.avgPriceMin, params.avgPriceMax]}
                      onChange={handleAvgPriceRangeChange}
                    />
                    <SquareFootRangeControl
                      min={500}
                      max={10_000}
                      step={50}
                      values={[params.squareFootMin, params.squareFootMax]}
                      onChange={handleSquareFootRangeChange}
                    />
                  </div>

                  {/* Left / right columns */}
                  <PriceChangeControl
                    label="3-Month Price Change ($)"
                    value={params.threeMonthPriceChange}
                    onChange={handleThreeMonthPriceChange}
                  />
                  <StoriesControl
                    initialValue={params.stories}
                    onChange={handleStoriesChange}
                  />

                  <PriceChangeControl
                    label="6-Month Price Change ($)"
                    value={params.sixMonthPriceChange}
                    onChange={handleSixMonthPriceChange}
                  />
                  <BedroomsControl
                    initialValue={params.bedrooms}
                    onChange={handleBedroomsChange}
                  />

                  <PriceChangeControl
                    label="9-Month Price Change ($)"
                    value={params.nineMonthPriceChange}
                    onChange={handleNineMonthPriceChange}
                  />
                  <BathsControl
                    initialValue={params.baths}
                    onChange={handleBathsChange}
                  />

                  <PriceChangeControl
                    label="12-Month Price Change ($)"
                    value={params.twelveMonthPriceChange}
                    onChange={handleTwelveMonthPriceChange}
                  />
                  <GarageSpacesControl
                    initialValue={params.garageSpaces}
                    onChange={handleGarageSpacesChange}
                  />

                  <PriceChangeControl
                    label="24-Month Price Change ($)"
                    value={params.twoYearPriceChange}
                    onChange={handleTwoYearPriceChange}
                  />
                  <GarageTypeControl
                    initialValue={
                      params.garageType === "Front" ||
                      params.garageType === "Cottage" ||
                      params.garageType === "Side" ||
                      params.garageType === "Split"
                        ? params.garageType
                        : undefined
                    }
                    onChange={handleGarageTypeChange}
                  />

                  <ActiveCompsControl
                    initialValue={params.activeComps ?? 0}
                    onChange={handleActiveCompsChange}
                    min={0}
                    max={500000}
                    step={1}
                  />
                  <CityControl
                    initialValue={
                      CITY_OPTIONS.includes(params.city)
                        ? params.city
                        : undefined
                    }
                    onChange={handleCityChange}
                  />
                </div>
              </CardContent>
            </Card>

            <hr style={{ borderColor: "var(--border-color)" }} />

            <Card
              className="shadow-md rounded-xl"
              style={{
                backgroundColor: "var(--subcard-bg)",
                color: "var(--text-color)",
                border: "1px solid var(--border-color)",
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: "var(--text-color)" }}>
                  Amenities
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <AmenityControl
                  selectedAmenities={params.amenities}
                  onChange={(updatedAmenities) =>
                    setParams((prev) => ({
                      ...prev,
                      amenities: updatedAmenities,
                    }))
                  }
                  clusterName={clusterName}
                  clusterDescription={clusterDescription}
                />
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
